/**
 * Shareable link utilities
 * Handles copying links to clipboard and generating shareable links
 */

/**
 * Copy text to clipboard
 * 
 * @param text - Text to copy
 * @returns Promise that resolves to true if successful, false otherwise
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    // Use modern Clipboard API if available
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      return successful;
    } catch (err) {
      document.body.removeChild(textArea);
      return false;
    }
  } catch (error) {
    console.error('Error copying to clipboard:', error);
    return false;
  }
}

/**
 * Generate shareable link for an event
 * 
 * @param eventId - Event ID
 * @returns Full shareable URL
 */
export function generateShareableLink(eventId: string): string {
  const origin = window.location.origin;
  return `${origin}/join/${eventId}`;
}

/**
 * Generate share text for an event
 * 
 * @param eventName - Event name
 * @param eventId - Event ID
 * @returns Share text message
 */
export function generateShareText(eventName: string, eventId: string): string {
  const link = generateShareableLink(eventId);
  return `Join my Fight Night Bookie: ${eventName}\n\n${link}`;
}

/**
 * Share via Web Share API (if available)
 * 
 * @param title - Share title
 * @param text - Share text
 * @param url - Share URL
 * @returns Promise that resolves to true if shared, false otherwise
 */
export async function shareViaWebShare(
  title: string,
  text: string,
  url: string
): Promise<boolean> {
  if (navigator.share) {
    try {
      await navigator.share({
        title,
        text,
        url,
      });
      return true;
    } catch (error) {
      // User cancelled or error occurred
      if ((error as Error).name !== 'AbortError') {
        console.error('Error sharing:', error);
      }
      return false;
    }
  }
  return false;
}




